/**
 * Shared feed / LiB / website media rules.
 *
 * Use these entry points everywhere — do not re-split `media_url` /
 * `thumbnail` with `.split(",")` or invent local video-vs-carousel checks:
 *
 * - {@link splitMediaUrls} — parse comma-separated media lists (CDN-safe)
 * - {@link isCarouselMediaPost} / {@link hasMultipleAbsoluteMediaUrls} —
 *   multi-slide albums
 * - {@link resolveFeedCardKind} — video vs multi-still vs image vs shoppable
 * - {@link resolveVideoPosterUrl} / {@link getThumbnail} —
 *   posters for video / per-slide carousel covers
 * - {@link resolvePlayableVideoUrl} — playable mp4/etc for hover players
 */

/**
 * True for URLs safe to use as durable `<img src>` / persisted media.
 * Rejects ephemeral browser previews (`blob:`, `data:`) that break outside
 * the tab that created them — a common product-tag paste bug.
 */
export function isUsableRemoteMediaUrl(url: string): boolean {
  const value = String(url || "").trim();
  if (!value) return false;
  if (/^(blob|data):/i.test(value)) return false;
  return /^https?:\/\//i.test(value);
}

export function splitMediaUrls(raw: string): string[] {
  const value = String(raw || "").trim();
  if (!value) return [];
  // Signed CDN URLs often include commas in query params. Only split when
  // the next chunk is another absolute URL.
  if (/^https?:\/\//i.test(value)) {
    return value
      .split(/,(?=https?:\/\/)/i)
      .map((part) => part.trim())
      .filter(Boolean);
  }
  return value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

export function isLikelyImageUrl(url: string): boolean {
  return /\.(avif|gif|jpe?g|png|svg|webp)(\?|#|$)/i.test(
    String(url || "").trim().toLowerCase(),
  );
}

export function isLikelyVideoUrl(url: string): boolean {
  const value = String(url || "").trim().toLowerCase();
  if (!value) return false;
  // Poster/thumbnail paths often include the word "video" (Cloudinary
  // `/video/upload/…jpg`, IG CDN folders, buckets named `instagram-videos`).
  // Those must still be usable as `<img src>`. Only real video files count.
  if (isLikelyImageUrl(value)) return false;
  return /\.(mp4|mov|webm|m3u8|m4v)(\?|#|$)/i.test(value);
}

/** Instagram / Facebook CDN stills expire quickly — prefer storage hosts. */
export function isInstagramCdnUrl(url: string): boolean {
  const value = String(url || "").trim().toLowerCase();
  if (!value) return false;
  try {
    const host = new URL(value).hostname;
    return host.includes("cdninstagram.com") || host.includes("fbcdn.net");
  } catch {
    return value.includes("cdninstagram.com") || value.includes("fbcdn.net");
  }
}

/**
 * Unique still-image URLs, durable hosts first (Supabase / S3), then IG CDN.
 * Never returns video files — those must not be used as `<img src>`.
 */
export function listRankedStillImageUrls(
  candidates: Array<string | null | undefined>,
): string[] {
  const seen = new Set<string>();
  const stills: string[] = [];
  for (const raw of candidates) {
    for (const part of splitMediaUrls(String(raw || ""))) {
      if (
        seen.has(part) ||
        !isUsableRemoteMediaUrl(part) ||
        isLikelyVideoUrl(part)
      ) {
        continue;
      }
      seen.add(part);
      stills.push(part);
    }
  }
  const durable = stills.filter((u) => !isInstagramCdnUrl(u));
  const ephemeral = stills.filter((u) => isInstagramCdnUrl(u));
  return [...durable, ...ephemeral];
}

export function pickBestStillImageUrl(
  candidates: Array<string | null | undefined>,
): string {
  return listRankedStillImageUrls(candidates)[0] || "";
}

/** Gray play-triangle fallback used by video tiles when no still exists. */
export const DUMMY_VIDEO_POSTER = "/assets/dummy_video.png";

function isUsableVideoPosterCandidate(url: string): boolean {
  const value = String(url || "").trim();
  if (!value || value.includes("dummy_video")) return false;
  // Relative app assets (e.g. local placeholders) are allowed; remote
  // posters must be http(s) stills — never a playable video file.
  if (/^https?:\/\//i.test(value)) {
    return isUsableRemoteMediaUrl(value) && !isLikelyVideoUrl(value);
  }
  return !isLikelyVideoUrl(value);
}

type VideoPosterPost = {
  media_url?: string | null;
  storage_url?: string | null;
  thumbnail?: string | null;
  thumbnail_url?: string | null;
  card_image?: string | null;
  table_image?: string | null;
  product_image?: string | null;
} | null | undefined;

/**
 * Ranked still candidates for a video tile poster (durable hosts first).
 * Prefers real video posters over product_image so shoppable-still routing
 * stays separate — but falls back to product/table stills when the only
 * media is a playable video (expired IG thumbs → blank individual tiles).
 */
export function listVideoPosterUrls(
  post: VideoPosterPost,
  explicitThumbnail?: string | null,
): string[] {
  const explicit = String(explicitThumbnail || "").trim();
  const primary = listRankedStillImageUrls([
    isUsableVideoPosterCandidate(explicit) ? explicit : "",
    post?.thumbnail,
    post?.thumbnail_url,
    post?.card_image,
    post?.media_url,
    post?.storage_url,
    post?.table_image,
  ]);
  if (primary.length) return primary;
  return listRankedStillImageUrls([post?.product_image, post?.table_image]);
}

/**
 * Poster/still for video tiles (LiB / website / individual).
 * Prefers an explicit thumbnail, then thumbnail fields, then still slides
 * inside media_url / storage_url (Instagram carousel albums often store
 * covers only there). Falls back to {@link DUMMY_VIDEO_POSTER}.
 */
export function resolveVideoPosterUrl(
  post: VideoPosterPost,
  explicitThumbnail?: string | null,
): string {
  return listVideoPosterUrls(post, explicitThumbnail)[0] || DUMMY_VIDEO_POSTER;
}

/**
 * Ranked poster candidates for one carousel slide (durable hosts first).
 * Indices stay aligned with media_url; video slides borrow neighboring stills.
 * Callers should walk this list on &lt;img&gt; error before showing the gray dummy.
 */
export function listIndexedVideoPosterUrls(
  post: VideoPosterPost,
  index: number,
): string[] {
  const slideIndex = Number.isFinite(index) ? Math.max(0, Math.floor(index)) : 0;
  const thumbs = splitMediaUrls(String(post?.thumbnail || ""));
  const mediaParts = splitMediaUrls(String(post?.media_url || ""));
  const neighbors: string[] = [];
  for (let i = slideIndex - 1; i >= 0; i -= 1) {
    if (isUsableVideoPosterCandidate(mediaParts[i])) neighbors.push(mediaParts[i]);
  }
  for (let i = slideIndex + 1; i < mediaParts.length; i += 1) {
    if (isUsableVideoPosterCandidate(mediaParts[i])) neighbors.push(mediaParts[i]);
  }
  // Do not short-circuit on thumbs[index] — expired IG CDN thumbs often beat
  // durable stills when returned first. Rank the full set instead.
  return listRankedStillImageUrls([
    thumbs[slideIndex],
    mediaParts[slideIndex],
    ...neighbors,
    post?.thumbnail,
    post?.thumbnail_url,
    post?.card_image,
    post?.media_url,
    post?.storage_url,
    post?.table_image,
  ]);
}

/**
 * Per-slide poster for multi-media carousel posts. Uses splitMediaUrls so
 * signed CDN query commas are not treated as separators.
 */
export function resolveIndexedVideoPosterUrl(
  post: VideoPosterPost,
  index: number,
): string {
  return listIndexedVideoPosterUrls(post, index)[0] || DUMMY_VIDEO_POSTER;
}

export function isPlayableVideoFile(url: string): boolean {
  return /\.(mp4|mov|webm|m3u8|m4v)(\?|#|$)/i.test(String(url || "").trim());
}

/** True when a shoppable card should show `product_image` (edit modal + dashboard). */
export function hasShoppableProductStill(
  post: { product_image?: unknown } | null | undefined,
  isShopLinks?: boolean | null,
): boolean {
  if (!isShopLinks || !post) return false;
  return Boolean(shoppableProductImageSrc(post));
}

/** Raw product still used by shoppable tiles — same source as the edit modal. */
export function shoppableProductImageSrc(post: any): string {
  if (!post || typeof post !== "object") return "";
  for (const field of [post.product_image, post.table_image]) {
    for (const part of splitMediaUrls(String(field || ""))) {
      if (
        part &&
        isUsableRemoteMediaUrl(part) &&
        !isPlayableVideoFile(part)
      ) {
        return part;
      }
    }
  }
  return "";
}

/** Ranked stills from a feed/story row — storage/media before product_image CDN. */
export function stillImageCandidatesFromPost(
  post: any,
  opts?: { preferProductImage?: boolean },
): string[] {
  if (!post || typeof post !== "object") return [];
  const ranked = listRankedStillImageUrls(
    isStoryLikePostType(post.type)
      ? [
          post.storage_url,
          post.thumbnail,
          post.thumbnail_url,
          post.media_url,
          post.card_image,
          post.table_image,
          post.product_image,
        ]
      : [
          post.storage_url,
          post.media_url,
          post.thumbnail,
          post.thumbnail_url,
          post.card_image,
          post.table_image,
          post.product_image,
        ],
  );
  if (!opts?.preferProductImage) return ranked;
  const product = shoppableProductImageSrc(post);
  if (!product) return ranked;
  return [product, ...ranked.filter((u) => u !== product)];
}

export function isDefiniteVideoPostType(type: string, platform?: string): boolean {
  const t = String(type || "").trim().toUpperCase();
  if (
    t === "TIKTOK" ||
    t === "REELS" ||
    t === "VIDEO" ||
    t === "PODCAST" ||
    t === "SHORT" ||
    t === "SHORTS"
  ) {
    return true;
  }
  const p = String(platform || "").trim().toLowerCase();
  return p === "tiktok" || p === "youtube" || p === "podcast";
}

export function isStoryLikePostType(type: string): boolean {
  const t = String(type || "").trim().toUpperCase();
  return t === "STORY" || t === "HIGHLIGHTS";
}

export function resolvePlayableVideoUrl(args: {
  mediaUrl?: string;
  storageUrl?: string;
  type?: string;
  platform?: string;
}): string {
  const mediaParts = splitMediaUrls(args.mediaUrl || "");
  for (const part of mediaParts) {
    if (isLikelyVideoUrl(part)) return part;
  }
  const storage = String(args.storageUrl || "").trim();
  if (isLikelyVideoUrl(storage)) return storage;

  const treatAsVideo =
    isDefiniteVideoPostType(args.type || "", args.platform) ||
    isStoryLikePostType(args.type || "");
  if (!treatAsVideo) return "";

  for (const part of mediaParts) {
    if (part && !isLikelyImageUrl(part)) return part;
  }
  if (storage && !isLikelyImageUrl(storage)) return storage;
  return "";
}

export function hasLikelyVideoMedia(
  mediaUrl: string,
  type?: string,
  platform?: string,
  storageUrl?: string,
): boolean {
  return Boolean(
    resolvePlayableVideoUrl({
      mediaUrl,
      storageUrl,
      type,
      platform,
    }),
  );
}

export function hasMultipleAbsoluteMediaUrls(raw?: string | null): boolean {
  const parts = splitMediaUrls(String(raw || ""));
  return parts.length > 1 && parts.every((url) => /^https?:\/\//i.test(url));
}

/** True when a post’s media_url is a multi-slide album (carousel). */
export function isCarouselMediaPost(
  post: { media_url?: string | null } | null | undefined,
): boolean {
  return hasMultipleAbsoluteMediaUrls(post?.media_url);
}

/** Absolute media URLs from a post row (CDN-safe split). */
export function listPostMediaUrls(
  post: { media_url?: string | null } | null | undefined,
): string[] {
  return splitMediaUrls(String(post?.media_url || ""));
}

/**
 * Per-slide poster for carousel / multi-media posts.
 * Canonical name used by LiB + FeedPost + edit modals — prefer this over
 * local `thumbnail.split(",")` helpers.
 */
export function getThumbnail(
  post: VideoPosterPost,
  index: number = 0,
): string {
  return resolveIndexedVideoPosterUrl(post, Number(index) || 0);
}

export type FeedCardKind =
  | "podcast"
  | "shoppable-still"
  | "video"
  | "multi-still"
  | "image";

/**
 * Single decision tree for dashboard / LiB / website / individual feed tiles.
 * Callers must not re-split media_url or invent video vs still rules.
 */
/** Carousel component types that show shoppable *posts*, not product-bank stills. */
export function isContentDayWindowComponentType(
  componentType?: string | null,
): boolean {
  const t = String(componentType || "")
    .trim()
    .toLowerCase();
  return (
    t === "yesterday" ||
    t === "this week" ||
    t === "this month" ||
    t === "this week content" ||
    t === "daywise"
  );
}

export function resolveFeedCardKind(
  post: {
    media_url?: string | null;
    storage_url?: string | null;
    product_image?: unknown;
    type?: string | null;
    platform?: string | null;
  } | null | undefined,
  options?: {
    isLinks?: boolean | null;
    componentType?: string | null;
    treatProductTypeAsStill?: boolean;
    isPodcast?: boolean;
  },
): FeedCardKind {
  if (!post) return "image";
  const componentType = String(options?.componentType || "").toLowerCase();
  const postType = isStoryLikePostType(String(options?.componentType || ""))
    ? String(options?.componentType)
    : String(post.type || options?.componentType || "");
  // Content / day-window carousels (Shop This Past Week, Yesterday, …) pass
  // isLinks for Shop CTAs — must NOT swap post media for product_image stills.
  // Trending Content is the same: shoppable TikToks must keep post media /
  // video, not Product Bank stills (edit preview looked "fishy" vs publish).
  const skipShopStill =
    componentType === "content" ||
    componentType === "query-content" ||
    componentType === "trending content" ||
    componentType === "trending-content" ||
    componentType === "content-series" ||
    isContentDayWindowComponentType(componentType) ||
    isStoryLikePostType(postType);

  if (options?.isPodcast || String(post.platform || "").toLowerCase() === "podcast") {
    return "podcast";
  }

  const platform = String(post.platform || "");
  const definiteVideo = isDefiniteVideoPostType(
    String(post.type || postType || ""),
    platform,
  );

  // Shoppable product stills must not replace actual video posts (REELS/VIDEO/
  // TIKTOK/…). Content day-windows often pass isLinks=true for Shop buttons;
  // preferring a broken/empty product_image left blank tiles above captions.
  if (
    !skipShopStill &&
    !definiteVideo &&
    (hasShoppableProductStill(post, options?.isLinks) ||
      (options?.treatProductTypeAsStill &&
        String(post.type || "").toUpperCase() === "PRODUCT"))
  ) {
    return "shoppable-still";
  }

  // Instagram carousel albums store comma-separated absolute URLs in media_url.
  // Prefer multi-still before video so a video slide (or REELS type) does not
  // collapse the album into a single VideoPostTag — ImagePostCarouselTag
  // already renders mixed image/video slides with dots.
  if (hasMultipleAbsoluteMediaUrls(post.media_url)) return "multi-still";

  if (
    hasLikelyVideoMedia(
      String(post.media_url || ""),
      postType,
      platform,
      String(post.storage_url || ""),
    )
  ) {
    return "video";
  }

  return "image";
}

/** True for YouTube platform posts (and common aliases / youtu.be permalinks). */
export function isYouTubePlatformPost(post: {
  platform?: string | null;
  permalink?: string | null;
  url?: string | null;
} | null | undefined): boolean {
  if (!post) return false;
  const platform = String(post.platform || "").trim().toLowerCase();
  if (platform === "youtube" || platform === "yt") return true;
  const link = String(post.permalink || post.url || "");
  return /(?:youtube\.com|youtu\.be)\//i.test(link);
}
